import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type PostRow = Database["public"]["Tables"]["posts"]["Row"];
type DbCategory = Database["public"]["Enums"]["post_category"];

/**
 * Public-facing article shape consumed by NewsCard, News list, LatestNews,
 * and the NewsArticle detail page. Adapts a DB `posts` row to a stable UI
 * contract so presentational components don't need to know about Supabase.
 */
export interface NewsArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  date: Date;
  category: string;
  /** Where the card / title links to. Internal: localized /news/:slug. External: source URL. */
  href: string;
  isExternal: boolean;
  image?: string;
  author?: string;
  bodyHtml?: string;
  coverImageUrl?: string | null;
  updatedAt?: string;
  externalUrl?: string | null;
}

/** Human-readable category label keys used by NewsCard / filters. */
const CATEGORY_LABELS: Record<DbCategory, string> = {
  article: "Article",
  insight: "Insight",
  event: "Event",
  case_study: "Case study",
};

const orderingDate = (row: PostRow): Date => {
  const iso = row.original_published_at ?? row.published_at ?? row.created_at;
  return new Date(iso);
};

/**
 * Map a DB row to the UI `NewsArticle` shape.
 * @param row Posts row
 * @param internalHref Pre-localized internal path (caller handles `localize()`).
 */
export const postRowToArticle = (row: PostRow, internalHref: string): NewsArticle => {
  const external = row.is_external;
  const externalUrl = row.external_linkedin_url ?? row.source_url ?? null;
  return {
    id: row.id,
    slug: row.slug,
    title: row.title,
    excerpt: row.excerpt ?? "",
    date: orderingDate(row),
    category: CATEGORY_LABELS[row.category] ?? row.category,
    // Always link to the on-domain detail page; external posts surface the
    // outbound LinkedIn CTA from the detail page itself.
    href: internalHref,
    isExternal: external,
    image: row.cover_image_url ?? undefined,
    author: row.source_author ?? row.author_name ?? undefined,
    bodyHtml: row.body_html,
    coverImageUrl: row.cover_image_url,
    updatedAt: row.updated_at,
    externalUrl,
  };
};

/** All published posts, newest first by effective publish date. */
export const fetchPublishedPosts = async (): Promise<PostRow[]> => {
  const { data, error } = await supabase
    .from("posts")
    .select("*")
    .eq("status", "published")
    .order("effective_published_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
};

/** N most recent published posts by effective publish date. */
export const fetchLatestPosts = async (limit: number): Promise<PostRow[]> => {
  const { data, error } = await supabase
    .from("posts")
    .select("*")
    .eq("status", "published")
    .order("effective_published_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
};

/** Single published post by slug. Returns null if not found. */
export const fetchPostBySlug = async (slug: string): Promise<PostRow | null> => {
  const { data, error } = await supabase
    .from("posts")
    .select("*")
    .eq("status", "published")
    .eq("slug", slug)
    .maybeSingle();
  if (error) throw error;
  return data;
};

